import { Tab, Tabs } from "react-bootstrap";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { Table } from "stories";
import { currencyStyle, parseCurrency } from "utils/customHooks/currencyFormat";
import { v4 as uuidv4 } from "uuid";

const tableHeaders = [
  {
    id: "title",
    title: "Uses of Funds",
    align: "left",
  },
  {
    id: "amount",
    title: "Amount",
    align: "right",
  },
];
export const usesRowsProps = {
  deletable: true,
  title: {
    editable: true,
  },
  amount: {
    editable: true,
    type: "currency",
  },
};
const getEmptyRow = () => ({
  uid: uuidv4(),
  title: "",
  amount: "",
  props: usesRowsProps,
});

// eslint-disable-next-line
const titlify = (key) => {
  return key.replaceAll("_", " ");
};

export const numberify = (amount) => {
  return String(parseCurrency(amount));
};

export const generateFundsRows = (funds) => {
  const getRowObj = (title, key, editable = false, tooltip = undefined) => {
    return {
      uid: key,
      title,
      amount: editable ? funds[key] : `$ ${currencyStyle(funds[key])}`,
      props: {
        tooltip,
        title: {
          className: "capitalize",
        },
        amount: {
          editable,
          type: "currency",
        },
      },
    };
  };
  return [
    { key: "property_acquisition", title: "Property Acquisition" },
    { key: "mortgage_payout", title: "Mortgage Payout Amount" },
    {
      key: "estimated_fundever_fees",
      title: "Estimated Fundever Fees*",
      tooltip: "0.02 * Requested Loan Amount",
    },
    {
      key: "estimated_lender_fees",
      title: "Estimated Lender Fees*",
      tooltip: "0.02 * Requested Loan Amount",
    },
    {
      key: "estimated_legal_fees",
      title: "Estimated Legal Fees*",
      tooltip: "0.002 * Requested Loan Amount",
    },
    { key: "interest_reserve", title: "Interest Reserve", editable: true },
    { key: "equity_takeout", title: "Equity Takeout Amount", editable: true },
  ].map((fund) => getRowObj(fund.title, fund.key, fund.editable, fund.tooltip));
};

export const UsesTable = ({ equalCalculation }) => {
  const form = useFormContext();
  const formData = useWatch({
    control: form.control,
    name: "uses",
  });
  const {
    fields: otherRows,
    append: addRow,
    remove: onDelete,
    update: updateCellValue,
  } = useFieldArray({
    control: form.control,
    name: "uses.other_uses",
    keyName: "key",
  });
  const handleAddRow = () => {
    addRow(getEmptyRow());
  };
  const tableRows = formData ? generateFundsRows(formData) : [];
  const mergedTableRows = [...tableRows, ...otherRows];

  const footerData = [
    {
      id: 1,
      title: "Total Uses of Funds",
      amount: () => {
        const total = mergedTableRows.reduce((prev, row) => {
          const amount = row.amount ? numberify(row.amount) : 0;
          if (!isNaN(Number(parseFloat(amount))))
            return prev + Number(parseFloat(amount));
          return prev;
        }, 0);
        return `$ ${currencyStyle(Number(total.toFixed(2)))}`;
      },
      props: {
        amount: {
          className: !equalCalculation ? "text-red" : "",
        },
      },
    },
  ];

  const handeTableValueChanged = (index, cellId, value) => {
    if (mergedTableRows[index].title === "Interest Reserve") {
      form.setValue("uses.interest_reserve", value);
    } else if (mergedTableRows[index].title === "Equity Takeout Amount") {
      form.setValue("uses.equity_takeout", value);
    } else {
      const i = index - tableRows.length;
      updateCellValue(i, {
        ...mergedTableRows[index],
        [cellId]: value,
      });
    }
  };
  const handleDelete = (index) => {
    onDelete(index - tableRows.length);
  };
  return (
    <Tabs id="controlled-tab-example" activeKey={"uses"}>
      <Tab eventKey="uses" title="Uses of Funds" tabClassName="text-left">
        <div className="pr-2/5">
          <Table
            headers={tableHeaders}
            rows={mergedTableRows}
            footer={footerData}
            onDelete={handleDelete}
            onFieldChanged={handeTableValueChanged}
          />
        </div>

        <div
          className={"fw-bold text-primary mb-3 d-inline-block"}
          role={"button"}
          onClick={handleAddRow}
        >
          + Add Use of Funds
        </div>
      </Tab>
    </Tabs>
  );
};
